import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { KeyboardKey } from 'common/common/constants/keyboard-key.enum';
import { KeyboardHandlerModel } from 'common/keyboard-service/keyboard-handler.model';
import { BcLocalKeyboardHandlerFactory } from 'common/keyboard-service/local-keyboard-handler.factory';
import { ExtendedPresence } from 'common/store/genesys-cloud/presence/models/extended-presence.model';
import { SystemPresenceType } from 'common/store/genesys-cloud/presence/models/system-presence.model';
import { GenesysCloudQueue } from 'common/store/genesys-cloud/queues/models/genesys-cloud-queue.model';
import { GenesysCloudUser } from 'common/store/genesys-cloud/users/models/user.model';
import { GenesysTransferPanelSearchAvailability } from './models/genesys-transfer-panel-search-availability.enum';

class Highlightable {
}

interface TransferPanelList {
  expanded: boolean;
  header: Highlightable;
  loadMore: Highlightable;
}

@Component({
  selector: 'app-genesys-transfer-panel-search-list',
  templateUrl: './genesys-transfer-panel-search-list.component.html',
  styleUrls: ['./genesys-transfer-panel-search-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysTransferPanelSearchListComponent implements OnInit {
  private keyboardHandler: KeyboardHandlerModel;

  @Input()
  users: GenesysCloudUser[];

  @Input()
  queues: GenesysCloudQueue[];

  @Input()
  canLoadMoreUsers: boolean;

  @Input()
  canLoadMoreQueues: boolean;

  @Input()
  keyboardHandlerElement: ElementRef;

  @Output()
  loadMoreUsers = new EventEmitter<void>();

  @Output()
  loadMoreQueues = new EventEmitter<void>();

  @Output()
  userSelected = new EventEmitter<GenesysCloudUser>();

  @Output()
  queueSelected = new EventEmitter<GenesysCloudQueue>();

  @Input()
  systemPresences: ExtendedPresence[];

  highlightedItem: Highlightable | GenesysCloudUser | GenesysCloudQueue;
  selectedItem: GenesysCloudUser | GenesysCloudQueue;

  availability: GenesysTransferPanelSearchAvailability.OFFLINE;

  queueList: TransferPanelList = {
    header: new Highlightable(),
    loadMore: new Highlightable(),
    expanded: false
  };

  userList: TransferPanelList = {
    expanded: false,
    header: new Highlightable(),
    loadMore: new Highlightable()
  };

  constructor(private readonly element: ElementRef,
    private readonly keyboardHandlerFactory: BcLocalKeyboardHandlerFactory,
    private readonly changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.keyboardHandler = this.keyboardHandlerFactory.createFromElement(this.keyboardHandlerElement.nativeElement);
    this.addKeyboardHandlers();
  }

  availableUsers(): number {
    const collectedAvailability = this.collectUserAvailability();
    return collectedAvailability[GenesysTransferPanelSearchAvailability.AVAILABLE] || 0;
  }

  toggleList(list: TransferPanelList): void {
    list.expanded = !list.expanded;
  }

  expandList(): void {
    if (this.highlightedItem === this.userList.header) {
      this.userList.expanded = true;
    } else if (this.highlightedItem === this.queueList.header) {
      this.queueList.expanded = true;
    }

    this.changeDetectorRef.detectChanges();
  }

  collapseList(): void {
    if (this.highlightedItem === this.userList.header) {
      this.userList.expanded = false;
    } else if (this.highlightedItem === this.queueList.header) {
      this.queueList.expanded = false;
    }

    this.changeDetectorRef.detectChanges();
  }

  highlightItem(item: GenesysCloudUser | GenesysCloudQueue | Highlightable): void {
    this.highlightedItem = item;
    this.changeDetectorRef.detectChanges();
  }

  isHighlighted(item: GenesysCloudUser | GenesysCloudQueue | Highlightable): boolean {
    return this.highlightedItem === item;
  }

  highlightNextItem(): void {
    const sequence = this.getItemSequence();
    const highlightedItemIndex = sequence.findIndex(i => this.highlightedItem === i);

    if (sequence[highlightedItemIndex + 1]) {
      this.highlightItem(sequence[highlightedItemIndex + 1]);
    }

    this.changeDetectorRef.detectChanges();
    this.scrollHighlightedElementIntoView(false);
  }

  highlightPreviousItem(): void {
    const sequence = this.getItemSequence();
    const highlightedItemIndex = sequence.findIndex(i => this.highlightedItem === i);

    if (sequence[highlightedItemIndex - 1]) {
      this.highlightItem(sequence[highlightedItemIndex - 1]);
    }

    this.changeDetectorRef.detectChanges();
    this.scrollHighlightedElementIntoView(true);
  }

  isItemHighlighted(item: GenesysCloudUser | GenesysCloudQueue | Highlightable): boolean {
    return this.highlightedItem === item;
  }

  isItemSelected(item: GenesysCloudUser | GenesysCloudQueue): boolean {
    return this.selectedItem === item;
  }

  selectItem(item: GenesysCloudUser | GenesysCloudQueue): void {
    if (this.selectedItem === item) {
      this.selectedItem = null;
    } else {
      this.selectedItem = item;
    }

    const user = this.users.find(u => u === item);
    const queue = this.queues.find(q => q === item);

    if (user) {
      this.userSelected.emit(<GenesysCloudUser>this.selectedItem);
    } else if (queue) {
      this.queueSelected.emit(<GenesysCloudQueue>this.selectedItem);
    }
  }

  interactWithHighlightedItem(): void {
    if (!(this.highlightedItem instanceof Highlightable)) {
      this.selectItem(this.highlightedItem);
    } else if (this.highlightedItem === this.userList.loadMore) {
      this.highlightPreviousItem();
      this.loadMoreUsers.emit();
    } else if (this.highlightedItem === this.queueList.loadMore) {
      this.highlightPreviousItem();
      this.loadMoreQueues.emit();
    }

    this.changeDetectorRef.detectChanges();
  }

  getUserAvailability(user: GenesysCloudUser): GenesysTransferPanelSearchAvailability {
    const systemPresenceId = user.presence?.presenceDefinition?.id;
    const mappedSystemPresence = this.systemPresences?.find(s => s.id === systemPresenceId)?.name;

    switch (mappedSystemPresence) {
      case SystemPresenceType.Available:
      case SystemPresenceType.OnQueue:
        return GenesysTransferPanelSearchAvailability.AVAILABLE;
      case SystemPresenceType.Away:
      case SystemPresenceType.Break:
      case SystemPresenceType.Idle:
      case SystemPresenceType.Meal:
      case SystemPresenceType.Training:
        return GenesysTransferPanelSearchAvailability.AWAY;
      case SystemPresenceType.Busy:
      case SystemPresenceType.Meeting:
        return GenesysTransferPanelSearchAvailability.BUSY;
      default:
        return GenesysTransferPanelSearchAvailability.OFFLINE;
    }
  }

  getUserListAvailability(): GenesysTransferPanelSearchAvailability {
    if (!this.users) {
      return GenesysTransferPanelSearchAvailability.OFFLINE;
    }

    const collectedUserAvailability = this.collectUserAvailability();

    if (collectedUserAvailability[GenesysTransferPanelSearchAvailability.AVAILABLE] > 0) {
      return GenesysTransferPanelSearchAvailability.AVAILABLE;
    } else if (collectedUserAvailability[GenesysTransferPanelSearchAvailability.AWAY] > 0) {
      return GenesysTransferPanelSearchAvailability.AWAY;
    } else if (collectedUserAvailability[GenesysTransferPanelSearchAvailability.BUSY] > 0) {
      return GenesysTransferPanelSearchAvailability.BUSY;
    }

    return GenesysTransferPanelSearchAvailability.OFFLINE;
  }

  private collectUserAvailability(): Map<GenesysTransferPanelSearchAvailability, number> {
    if (!this.users) {
      return new Map<GenesysTransferPanelSearchAvailability, number>();
    }

    const results = this.users.reduce((map, user) => {
      map[this.getUserAvailability(user)] = (map[this.getUserAvailability(user)] || 0) + 1;
      return map;
    }, new Map<GenesysTransferPanelSearchAvailability, number>());

    return results;
  }

  private scrollHighlightedElementIntoView(scrollToTop: boolean) {
    const currentListItem = this.element.nativeElement.querySelector(`.gc-transfer-panel-list-item.highlighted`);
    let isElementOutsideViewport = false;

    const scrollElement = this.element.nativeElement;

    let elementBounds = currentListItem.getBoundingClientRect();
    let viewportBounds = scrollElement.getBoundingClientRect();

    if (scrollToTop) {
      isElementOutsideViewport = (elementBounds.top < viewportBounds.top);
    } else {
      isElementOutsideViewport = (elementBounds.bottom > viewportBounds.bottom);
    }

    if (isElementOutsideViewport) {
      if (scrollToTop) {
        currentListItem.scrollIntoView(true);
      } else {
        currentListItem.scrollIntoView(false);
      }
    }
  }

  private addKeyboardHandlers(): void {
    const getLowercaseKeyboardKey = (keyboardKey: KeyboardKey): string => {
      return keyboardKey.toLowerCase();
    };

    this.keyboardHandler.addKeyHandler(getLowercaseKeyboardKey(KeyboardKey.Up), () => this.highlightPreviousItem(), {});
    this.keyboardHandler.addKeyHandler(getLowercaseKeyboardKey(KeyboardKey.Down), () => this.highlightNextItem(), {});
    this.keyboardHandler.addKeyHandler(getLowercaseKeyboardKey(KeyboardKey.Left), () => this.collapseList(), {});
    this.keyboardHandler.addKeyHandler(getLowercaseKeyboardKey(KeyboardKey.Right), () => this.expandList(), {});
    this.keyboardHandler.addKeyHandler(getLowercaseKeyboardKey(KeyboardKey.Enter), () => this.interactWithHighlightedItem(), {});
  }

  private getItemSequence() {
    const sequence = [];

    sequence.push(this.userList.header);
    if (this.userList.expanded) {
      sequence.push(...this.users);

      if (this.canLoadMoreUsers) {
        sequence.push(this.userList.loadMore);
      }
    }

    sequence.push(this.queueList.header);
    if (this.queueList.expanded) {
      sequence.push(...this.queues);

      if (this.canLoadMoreQueues) {
        sequence.push(this.queueList.loadMore);
      }
    }

    return sequence;
  }
}
